/* oxlint-disable react/jsx-props-no-spreading -- wizard steps are rendered dynamically with config-driven props */
import { useCallback, useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useLocation, useNavigate } from 'react-router-dom';
import { Container } from 'reactstrap';
import { v4 as uuidv4 } from 'uuid';
import { Form, Formik, type FormikErrors, type FormikProps } from 'formik';
import type {
  HandleRequestOffer,
  WizardFormValues,
} from '../../../../domain/customerInquiry';
import validationSchema from '../../FormValidation/ValidationSchema';
import StepIndicator from '../../../../common/StepIndicator';
import Button from '../../../../common/Button';
import LoadingSpinner from '../../../../common/LoadingSpinner';
import buttonStyles from '../../../../common/Button/Button.module.scss';
import { ArrowLeft2, ArrowLeft3 } from '../../../../assets/icons';
import s from './OfferGenerationWizard.module.scss';
import {
  errorToTouchedAndIds,
  toFormikTouched,
} from '../../../../common/FormFields/formUtils';
import RequiredFieldInfo from '../../ContactPageShell/RequiredFieldInfo';
import {
  findWizardStructureNode,
  getComponentClassAndProps,
  getFormStepAndPathFromLocation,
  getNewLocationFromState,
  getNextOrBackFromLocationDiff,
  validPathSelected,
} from '../utils/offerGenerationWizardUtils';
import {
  coerceToString,
  isFormikFieldErrorSection,
} from '../../../../utils/coerce';

interface OfferGenerationWizardProps {
  handleRequestOffer: HandleRequestOffer;
}

function buildInitialWizardValues(
  initialFormValues: Partial<WizardFormValues>,
  initialStep: number
): WizardFormValues {
  return {
    category: initialFormValues.category ?? '',
    product: initialFormValues.product ?? '',
    facilityType: initialFormValues.facilityType,
    certificate: initialFormValues.certificate,
    powerType: initialFormValues.powerType,
    windFacilityData: initialFormValues.windFacilityData ?? [{ id: uuidv4() }],
    waterFacilityData: initialFormValues.waterFacilityData ?? [
      { id: uuidv4() },
    ],
    solarFacilityData: initialFormValues.solarFacilityData ?? [
      { id: uuidv4() },
    ],
    bioMasseFacilityData: initialFormValues.bioMasseFacilityData ?? [
      { id: uuidv4() },
    ],
    information: initialFormValues.information ?? {
      country: { id: 'de', name: 'Deutschland' },
    },
    individualOffer: initialFormValues.individualOffer ?? {
      firstName: '',
      lastName: '',
      salutation: null,
      position: '',
      phone: '',
      email: '',
      privacyPolicy: false,
    },
    greenPowerData: {
      wind: false,
      water: false,
      photovoltaic: false,
      biomass: false,
      swap: false,
      ...initialFormValues.greenPowerData,
    },
    greyPowerData: initialFormValues.greyPowerData ?? {},
    businessChargingData: initialFormValues.businessChargingData ?? {},
    immoChargingData: initialFormValues.immoChargingData ?? {},
    coopChargingData: initialFormValues.coopChargingData ?? {},
    largeSolarFacilityData: initialFormValues.largeSolarFacilityData ?? {},
    co2CertificateData: initialFormValues.co2CertificateData ?? {},
    proofOfOriginCertificateData:
      initialFormValues.proofOfOriginCertificateData ?? {},
    step: initialStep,
  };
}

function OfferGenerationWizard({
  handleRequestOffer,
}: OfferGenerationWizardProps) {
  const intl = useIntl();
  const location = useLocation();
  const navigate = useNavigate();
  const formikRef = useRef<FormikProps<WizardFormValues> | null>(null);
  const stepIndicatorRef = useRef<HTMLDivElement | null>(null);
  const [initialFormValues, initialStep, initialPath] =
    getFormStepAndPathFromLocation(location, true);
  const [currentStep, setCurrentStep] = useState(initialStep);
  const [currentPath, setCurrentPath] = useState(initialPath);
  const [submissionError, setSubmissionError] = useState<unknown>(null);
  const [submitted, setSubmitted] = useState(false);
  const [isSolarFacilitySoldOut, setIsSolarFacilitySoldOut] = useState(false);
  const [isGreenPowerConsumptionTooSmall, setisGreenPowerConsumptionTooSmall] =
    useState(false);
  const [isGreyPowerConsumptionTooSmall, setIsGreyPowerConsumptionTooSmall] =
    useState(false);
  const [isFirstDisplay, setIsFirstDisplay] = useState(true);

  const prevStepRef = useRef<number | undefined>(undefined);
  const formikPropsRef = useRef<FormikProps<WizardFormValues> | null>(null);
  const isInternalNavigationRef = useRef(false);

  useEffect(() => {
    prevStepRef.current = currentStep;
  });
  const prevStep = prevStepRef.current;

  const goBack = useCallback(
    (formikProps: FormikProps<WizardFormValues>) => {
      isInternalNavigationRef.current = true;
      let newStep = currentStep - 1;
      const { skipThisStep } =
        findWizardStructureNode(newStep, currentPath) ?? {};
      if (skipThisStep) {
        newStep -= 1;
      }
      void formikProps.setFieldValue('step', newStep);
      setCurrentStep(newStep);
    },
    [currentPath, currentStep]
  );

  const validateManually = async () => {
    let result = false;
    if (formikPropsRef.current) {
      const errors: FormikErrors<WizardFormValues> =
        await formikPropsRef.current.validateForm();
      const largeSolarErrors = isFormikFieldErrorSection(
        errors.largeSolarFacilityData
      )
        ? errors.largeSolarFacilityData
        : undefined;
      if (largeSolarErrors?.isSolarFacilitySoldOut) {
        setIsSolarFacilitySoldOut(true);
      } else {
        setIsSolarFacilitySoldOut(false);
      }
      const greenPowerErrors = isFormikFieldErrorSection(errors.greenPowerData)
        ? errors.greenPowerData
        : undefined;
      if (greenPowerErrors?.isGreenPowerConsumptionTooSmall) {
        setisGreenPowerConsumptionTooSmall(true);
      } else {
        setisGreenPowerConsumptionTooSmall(false);
      }

      const greyPowerErrors = isFormikFieldErrorSection(errors.greyPowerData)
        ? errors.greyPowerData
        : undefined;
      if (greyPowerErrors?.isGreyPowerConsumptionTooSmall) {
        setIsGreyPowerConsumptionTooSmall(true);
      } else {
        setIsGreyPowerConsumptionTooSmall(false);
      }

      const { touchedObj, errorIds } = errorToTouchedAndIds(errors);
      void formikPropsRef.current.setTouched(
        toFormikTouched<WizardFormValues>(touchedObj)
      );
      if (Object.keys(errors).length === 0) {
        result = true;
      } else if (errorIds.length > 0) {
        const selector = errorIds.map((k) => `[id='${k}']`).join(', ');
        const errorElements = document.querySelectorAll(selector);
        const firstError = errorElements[0];
        if (firstError instanceof HTMLElement) {
          firstError.focus();
        }
        result = false;
      }
    }
    return result;
  };

  const goToNext = async (selectedValue?: string) => {
    const node = findWizardStructureNode(currentStep, currentPath);
    if (!node) {
      return;
    }
    const { fieldName, map, noMapNeeded } = node;

    const newPath = [...currentPath];
    let newStep = currentStep + 1;

    if (formikPropsRef.current) {
      const valid = await validateManually();
      if (!valid) {
        return;
      }
    }

    if (noMapNeeded) {
      newPath[currentStep] = 0;
    } else if (fieldName && map && formikPropsRef.current) {
      const fieldValue =
        selectedValue ??
        coerceToString(formikPropsRef.current.values[fieldName]);
      newPath[currentStep] = map[fieldValue];
    }

    const newNode = findWizardStructureNode(newStep, newPath);
    if (newNode?.skipThisStep) {
      newStep += 1;
      newPath[currentStep + 1] = 0;
    }

    isInternalNavigationRef.current = true;
    setCurrentPath(newPath);
    setCurrentStep(newStep);
    if (formikPropsRef.current) {
      void formikPropsRef.current.setFieldValue('step', newStep);
    }
  };

  useEffect(() => {
    if (
      stepIndicatorRef.current &&
      !isFirstDisplay &&
      prevStep !== currentStep
    ) {
      stepIndicatorRef.current.focus();
    }
    setIsFirstDisplay(false);
  }, [prevStep, currentStep, isFirstDisplay]);

  useEffect(() => {
    if (formikRef.current && isInternalNavigationRef.current) {
      const formValues = formikRef.current.values;
      const newLocation = getNewLocationFromState(
        currentStep,
        currentPath,
        formValues
      );
      if (location.pathname !== newLocation) {
        void navigate(newLocation);
      } else {
        isInternalNavigationRef.current = false;
      }
    }
  }, [currentStep, currentPath, location.pathname, navigate]);

  useEffect(() => {
    if (formikRef.current && !isInternalNavigationRef.current) {
      const { pathname } = location;
      const formValues = formikRef.current.values;
      const stateLocation = getNewLocationFromState(
        currentStep,
        currentPath,
        formValues
      );
      if (stateLocation !== pathname) {
        const direction = getNextOrBackFromLocationDiff(
          stateLocation,
          pathname
        );
        if (direction === 'back' && currentStep > 0) {
          goBack(formikRef.current);
        } else {
          void navigate(stateLocation, { replace: true });
        }
      }
    }
  }, [currentPath, currentStep, goBack, location, navigate]);

  const handleSubmit = async (
    values: WizardFormValues,
    { setSubmitting }: { setSubmitting: (isSubmitting: boolean) => void }
  ) => {
    setSubmissionError(null);
    const error = await handleRequestOffer(values, setSubmitting);
    if (!error) {
      setSubmitted(true);
    } else {
      setSubmissionError(error);
    }
    setSubmitting(false);
  };

  const nextStepIsSubmit = currentStep === 5;
  const currentStepIsOffer = currentStep === 5;
  const currentStepIsFirstStep = currentStep === 0;
  const showRequiredFieldInfo = currentStep >= 3;
  const wizardNode = getComponentClassAndProps(currentStep, currentPath);
  const ComponentClass = wizardNode?.componentClass;
  const componentProps = wizardNode?.props ?? {};
  const stepIndicatorLabel = wizardNode?.stepIndicatorLabel ?? '';

  return (
    <Container tag="main" className={s.container}>
      <Formik
        innerRef={formikRef}
        initialValues={buildInitialWizardValues(initialFormValues, initialStep)}
        onSubmit={handleSubmit}
        validationSchema={validationSchema(intl)}
      >
        {(formikProps) => {
          const { values, isSubmitting, submitForm } = formikProps;
          formikPropsRef.current = formikProps;
          const isPathValid = validPathSelected(
            currentStep,
            currentPath,
            values
          );

          return (
            <Form autoComplete="on" noValidate>
              <div className={s.wizard}>
                <StepIndicator
                  total={6}
                  step={currentStep + 1}
                  className={s.stepIndicator}
                  ref={stepIndicatorRef}
                  label={
                    stepIndicatorLabel
                      ? intl.formatMessage({ id: stepIndicatorLabel })
                      : undefined
                  }
                />
                {ComponentClass && (
                  <ComponentClass
                    {...componentProps}
                    values={values}
                    form={formikProps}
                    submitted={submitted}
                    onNext={goToNext}
                    isSolarFacilitySoldOut={isSolarFacilitySoldOut}
                    isGreenPowerConsumptionTooSmall={
                      isGreenPowerConsumptionTooSmall
                    }
                    isGreyPowerConsumptionTooSmall={
                      isGreyPowerConsumptionTooSmall
                    }
                  />
                )}
                {!currentStepIsOffer && (
                  <div className={s.wizardNavigation}>
                    {!currentStepIsFirstStep && (
                      <Button
                        variant="secondary"
                        type="button"
                        onClick={() => goBack(formikProps)}
                      >
                        <FormattedMessage id="offerWizard.backButton" />
                      </Button>
                    )}
                    {nextStepIsSubmit ? (
                      <>
                        <button
                          type="submit"
                          className={`${buttonStyles.button} ${buttonStyles.primary}`}
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? (
                            'Angebot wird erstellt...'
                          ) : (
                            <FormattedMessage id="offerWizard.nextButton" />
                          )}
                        </button>
                        {submissionError && (
                          <p style={{ color: 'red' }}>
                            Fehler bei der Angebotserstellung
                          </p>
                        )}
                      </>
                    ) : (
                      currentStep > 2 && (
                        <Button
                          disabled={!isPathValid}
                          variant="primary"
                          type="button"
                          onClick={() => goToNext()}
                        >
                          <FormattedMessage id="offerWizard.nextButton" />
                        </Button>
                      )
                    )}
                  </div>
                )}
                <div role="region" aria-live="polite">
                  {currentStepIsOffer && submitted && (
                    <div className={s.submitted}>
                      <FormattedMessage id="offerWizard.submitted" />
                    </div>
                  )}
                </div>
                {currentStepIsOffer && (
                  <div className={s.individualOfferButtons}>
                    <Button
                      variant="textOnly"
                      type="button"
                      onClick={() => goBack(formikProps)}
                      className={s.changeMyDataButton}
                      disabled={submitted || isSubmitting}
                    >
                      {submitted || isSubmitting ? (
                        <ArrowLeft3 />
                      ) : (
                        <ArrowLeft2 />
                      )}
                      <span>
                        <FormattedMessage id="offerWizard.StepIndividualOffer.changeMyData" />
                      </span>
                    </Button>
                    <Button
                      variant="primary"
                      type="submit"
                      className={s.requestOfferButton}
                      disabled={submitted || isSubmitting}
                      onClick={async () => {
                        await validateManually();
                        await submitForm();
                      }}
                    >
                      <div className={s.requestOfferButtonContent}>
                        <div>
                          <FormattedMessage id="offerWizard.StepIndividualOffer.requestIndividualOffer" />
                        </div>
                        <div
                          role="region"
                          aria-live="polite"
                          className={s.loadingSpinner}
                        >
                          {isSubmitting && <LoadingSpinner color="grey" />}
                        </div>
                      </div>
                    </Button>
                  </div>
                )}
                {showRequiredFieldInfo && <RequiredFieldInfo />}
              </div>
            </Form>
          );
        }}
      </Formik>
    </Container>
  );
}

export default OfferGenerationWizard;
